import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'

import store from '@/state/store'
import routes from './routes'
import { permissionManager } from '@/utils/permission'
import {
  routeRequiresPermission,
  checkRoutePermission,
  forbiddenRouteLocation,
} from '@/utils/routePermissions'
import { hasAcceptedLatestAgreement, shouldEnforceAgreementForUser } from '@/utils/agreement'
import { readerDebugLock, warnReaderDebugLocked } from '@/utils/readerDebug'
import { Message } from 'element-ui'
import i18n from '@/i18n'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

async function guardPermission(routeTo, next) {
  if (routeRequiresPermission(routeTo) && !checkRoutePermission(routeTo)) {
    return next(forbiddenRouteLocation(routeTo))
  }
  next()
}

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  if (readerDebugLock.active && routeTo.path !== "/console") {
    warnReaderDebugLocked(() => Message.warning(i18n.t("console.reader.locked")))
    return next(false)
  }
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

    if (!authRequired) return next()

    if (store.getters['auth/loggedIn']) {
      return store.dispatch('auth/validate').then(async (validUser) => {
        if (validUser) {
          await permissionManager.ensureReady()
          return guardPermission(routeTo, next)
        }
        redirectToLogin()
      })
    }

    redirectToLogin()

    function redirectToLogin() {
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = localStorage.getItem('user');

    if (authpage && !loggeduser) {
      return next('/login');
    }

    const continueWithPermissionCheck = async () => {
      if (authpage && loggeduser) {
        await permissionManager.ensureReady()
      }
      return guardPermission(routeTo, next)
    };

    if (authpage && loggeduser) {
      try {
        const currentUser = JSON.parse(loggeduser);
        const permissionUser = permissionManager.user;

        if (!permissionUser || currentUser.id !== permissionUser.id) {
          permissionManager._readyPromise = null;
          permissionManager.loadUser();
        }

        const isAgreementPage = routeTo.name === 'agreement-consent';
        const shouldSkipAgreementCheck = routeTo.path === '/logout';
        const shouldCheckAgreement = shouldEnforceAgreementForUser(currentUser);
        if (!isAgreementPage && !shouldSkipAgreementCheck && shouldCheckAgreement) {
          return hasAcceptedLatestAgreement(currentUser)
            .then((accepted) => {
              if (!accepted) {
                return next({
                  name: 'agreement-consent',
                  query: { redirectFrom: routeTo.fullPath },
                });
              }
              return continueWithPermissionCheck();
            })
            .catch(() => continueWithPermissionCheck());
        }
      } catch (error) {
        console.error('解析用户信息失败:', error);
        permissionManager._readyPromise = null;
        permissionManager.loadUser();
      }
    }

    return continueWithPermissionCheck();
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          resolve()
        }
      })
    }
  } catch (error) {
    return
  }

  next()
})

export default router
